import CardSymphony from 'elements/CardSymphony';
import { calculateThemeColors, clsx, styx } from 'modules/Utils';
import { CSSProperties, useState } from 'react';
import WebsiteBuilderHeader from '../WebsiteBuilderHeader';
import ButtonSymphony from 'elements/ButtonSymphony';
import useWebsiteBuilder, { VideoContentStyle, WebsiteBuilder_videosExists } from 'Hooks/useWebsiteBuilder';
import CloudinaryUploadWidget from 'components/shareable/CloudinaryUploadWidget';
import Sty from 'modules/Sty';
import OptionsSymphony, { OptionItem } from 'elements/OptionsSymphony';
import ModalSymphony from 'elements/ModalSymphony';
import InputSymphony from 'elements/InputSymphony';
import VideoSelector from 'components/shareable/VideoSelector';
import Icon from 'elements/Icon';
import useScreen from 'Hooks/useScreen';
import { isYouTubeVideoUrl, pullVideoIdFromUrl, queryYouTubeApi } from 'helpers/YouTube';
import Colors from 'modules/Colors';
import WebsiteBuilderFooter from '../WebsiteBuilderFooter';
import WebsiteBuilderEmptyState from '../WebsiteBuilderEmptyState';
import { getPalette } from 'react-palette';

interface VideoContentProps {
    className: string;
    contentIndex: number;
    tabSelectorComponent: React.ReactNode;
    id: string;
}

const VideoContent = ({
    className,
    contentIndex,
    tabSelectorComponent,
    id,
}: VideoContentProps) => {
    const { mobileView } = useScreen();
    const {
        websiteContent,
        setWebsiteContent,
        websiteStyle,
        setWebsiteStyle,
    } = useWebsiteBuilder() as WebsiteBuilder_videosExists;

    const websiteContentId = websiteContent[contentIndex].id;

    const videoContent = websiteContent[contentIndex].videoContent;
    const videoContentStyle = videoContent.style;
    const requiresPaidPlan = videoContentStyle === VideoContentStyle.FULL_SCREEN || videoContentStyle === VideoContentStyle.ROUNDED;

    const uploadButtonId = `videoContentButton_${contentIndex}_${websiteContentId}`;

    const YOUTUBE_CONNECTED = true;

    const isYouTubeVideo = videoContent.video.includes('youtube.com') || videoContent.video.includes('youtu.be');

    const imageOptionStyle: CSSProperties = {
        width: 30,
        height: 30,
        objectFit: 'cover',
        backgroundColor: Colors.greyDark,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
    }

    const VIDEO_STYLE_OPTIONS: OptionItem[] = [
        {
            id: VideoContentStyle.SQUARE,
            label: 'Square',
            childrenLeft: (
                <div
                    style={styx(imageOptionStyle, {
                        backgroundImage: `url(${videoContent.thumbnail})`,
                    })}
                />
            ),
        },
        {
            id: VideoContentStyle.ROUNDED,
            label: 'Rounded',
            iconRight: 'gem',
            childrenLeft: (
                <div
                    style={styx(imageOptionStyle, {
                        borderRadius: 5,
                        backgroundImage: `url(${videoContent.thumbnail})`,
                    })}
                />
            ),
        },
        {
            id: VideoContentStyle.FULL_SCREEN,
            label: 'Full Screen',
            iconRight: 'gem',
            childrenLeft: (
                <Icon
                    className="videoContentFullScreenIcon"
                    style={{
                        marginLeft: -4,
                        marginRight: -8,
                    }}
                    icon="expand"
                    size={30}
                    color={videoContentStyle === VideoContentStyle.FULL_SCREEN ? Colors.purple : Colors.greyDark}
                />
            ),
        },
        // {
        //     id: VideoContentStyle.CIRCULAR,
        //     label: 'Circular',
        //     childrenLeft: (
        //         <div
        //             style={styx(imageOptionStyle, {
        //                 backgroundImage: `url(${videoContent.thumbnail})`,
        //             })}
        //         />
        //     ),
        // },
    ];

    const [showReplaceVideoModal, setShowReplaceVideoModal] = useState<boolean>(false);
    const [youTubeUrlText, setYouTubeUrlText] = useState<string | null>(null);
    const [youTubeSearchResults, setYouTubeSearchResults] = useState<any[]>([]);
    const [isLoadingYoutubeSearch, setIsLoadingYoutubeSearch] = useState<boolean>(false);

    const updateVideoContent = async (video: string, thumbnail: string) => {
        const updatedContent = [...websiteContent]
        updatedContent[contentIndex].videoContent.video = video;
        updatedContent[contentIndex].videoContent.thumbnail = thumbnail;
        setWebsiteContent(updatedContent);

        if (contentIndex === 0) {
            const paletteData = await getPalette(thumbnail);
            const {
                backgroundColor,
                secondaryColor,
                textColor
            } = calculateThemeColors(paletteData);

            setWebsiteStyle({
                ...websiteStyle,
                backgroundColor,
                secondaryColor,
                textColor,
            });
        }
    }

    const handleOnUploadSuccess = async (file: { info: { secure_url: string, thumbnail_url: string } }) => {
        const video = file.info.secure_url;
        const thumbnail = file.info.thumbnail_url;

        await updateVideoContent(video, thumbnail);
        setShowReplaceVideoModal(false);
    }

    const handleYouTubeSearch = async () => {
        if (!youTubeUrlText) {
            return;
        }

        const isVideoUrl = isYouTubeVideoUrl(youTubeUrlText)
        if (!isVideoUrl || youTubeUrlText.length < 3) {
            return
        }

        setIsLoadingYoutubeSearch(true)

        // first confirm if its a channel or just a string
        if (isVideoUrl) {
            const videoId = pullVideoIdFromUrl(youTubeUrlText)

            const results = await queryYouTubeApi({
                endpoint: '/videos',
                params: {
                    id: videoId,
                    part: 'id,snippet',
                    maxResults: 15,
                }
            })

            const items = results?.items || []

            if (items.length) {
                const formattedItems = items.map((o: any) => {
                    return {
                        ...o.snippet,
                        id: o.id
                    }
                })
                setYouTubeSearchResults(formattedItems)
            }
        } else {
            const results = await queryYouTubeApi({
                endpoint: '/search',
                params: {
                    type: 'video',
                    q: youTubeUrlText,
                    part: 'id,snippet',
                    maxResults: 15,
                }
            })
            const items = results?.items || []

            if (items.length) {
                const formattedItems = items.map((o: any) => {
                    return {
                        ...o.snippet,
                        id: o.id.videoId
                    }
                })
                setYouTubeSearchResults(formattedItems)
            }
        }
        setIsLoadingYoutubeSearch(false)
    }

    const renderVideoDivider = () => {
        return (
            <div
                className="videoContentDividerContainer"
                style={sty.videoContentDividerContainer}
            >
                <div style={sty.videoContentDividerLine} />
                <div style={sty.videoContentDividerText}>
                    OR
                </div>
                <div style={sty.videoContentDividerLine} />
            </div>
        )
    }

    const renderFileUploadButton = () => {
        return (
            <>
                <ButtonSymphony
                    className="videoContentButton"
                    id={uploadButtonId}
                    iconLeft="video"
                    style={{
                        marginLeft: 'auto',
                        marginRight: 'auto',
                    }}
                >
                    Upload Video
                </ButtonSymphony>
            </>
        )
    }

    const renderYouTubeUrlInput = () => {
        return (
            <div
                className="videoContentYouTubeUrlInputContainer"
                style={sty.videoContentYouTubeUrlInputContainer}
            >
                <div className="youtubeSearchContainer" style={sty.youtubeSearchContainer}>
                    {!mobileView && <Icon className="youtubeIcon" icon="youtube" size={48} />}
                    <InputSymphony
                        className="videoSelectorInput"
                        placeholder={`Enter your YouTube video URL ${YOUTUBE_CONNECTED ? 'or select a video from your channel.' : '.'}`}
                        value={youTubeUrlText || ''}
                        onChange={(value) => setYouTubeUrlText(value)}
                        label="YouTube Video URL"
                        isSearch
                        onSearch={handleYouTubeSearch}
                        onEnter={handleYouTubeSearch}
                    />
                </div>
                <VideoSelector
                    youtubeConnected={true}
                    connectYoutubeButton={() => { }}
                    loadingSearch={isLoadingYoutubeSearch}
                    searchString={youTubeUrlText || ''}
                    content={youTubeSearchResults}
                    onVideoSelected={(videoDetails) => {
                        const videoUrl = videoDetails.url;
                        const thumbnail = videoDetails.thumbnails.high.url;

                        const updatedContent = [...websiteContent];
                        updatedContent[contentIndex].videoContent.video = videoUrl;
                        updatedContent[contentIndex].videoContent.thumbnail = thumbnail;
                        setWebsiteContent(updatedContent);

                        setShowReplaceVideoModal(false);
                    }}
                />
            </div>
        )
    }

    const renderRightSideContent = () => {
        if (mobileView) {
            return (
                <Icon
                    className="videoContentButton"
                    icon="video"
                    size={24}
                    onClick={() => setShowReplaceVideoModal(true)}
                />
            )
        }

        return (
            <ButtonSymphony
                className="videoContentButton"
                onClick={() => setShowReplaceVideoModal(true)}
            >
                Choose Video
            </ButtonSymphony>
        )
    }

    // NOTE: render()
    return (
        <>
            <CardSymphony
                className={clsx("videoContentComponent", className)}
                style={styx(sty.videoContentComponent)}
                size="small"
                id={id}
            >
                <WebsiteBuilderHeader
                    contentIndex={contentIndex}
                    rightSideContent={
                        <>
                            {/* {renderRightSideContent()} */}
                            {tabSelectorComponent}
                        </>
                    }
                />
                <CloudinaryUploadWidget
                    uploadButtonId={uploadButtonId}
                    resourceType="video"
                    maxFiles={1}
                    showUploadMoreButton={false}
                    clientAllowedFormats={["mp4"]}
                    minImageWidth={300}
                    onUploadAdded={(_fileId: string) => { }}
                    onUploadSuccess={handleOnUploadSuccess}
                    onUploadAbortAll={() => { }}
                    onUploadAbort={(_fileId: string) => { }}
                    onCancel={(_info: Record<string, unknown>) => { }}
                />

                <div
                    className="videoContentTab"
                    style={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 16,
                    }}
                >
                    <div style={Sty.text16grey}>
                        Select the video you want to display. We recommend using at least a video that's 1000x1000.
                    </div>
                    {!videoContent.video &&
                        <WebsiteBuilderEmptyState
                            className="videoContentEmptyState"
                            title="No video selected"
                            description="Select a video to display on your website."
                            isHorizontal
                            buttonText="Choose Video"
                            onClickButton={() => setShowReplaceVideoModal(true)}
                        />
                    }
                    {videoContent.video &&
                        <div
                            className="imageContentArtworkPreviewContainer"
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                border: Sty.border,
                                borderRadius: 16,
                                padding: 16,
                            }}
                        >
                            {isYouTubeVideo ?
                                <img
                                    src={videoContent.thumbnail}
                                    alt="YouTube Thumbnail"
                                    style={{
                                        width: 75,
                                        height: 75,
                                        objectFit: 'cover',
                                        borderRadius: 16,
                                        overflow: 'hidden',
                                    }}
                                />
                                :
                                <video
                                    className="videoPreview"
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    style={{
                                        width: 75,
                                        height: 75,
                                        objectFit: 'cover',
                                        borderRadius: 8,
                                        overflow: 'hidden',
                                    }}
                                    src={videoContent.video}
                                />
                            }
                            <div
                                style={{
                                    ...Sty.text16greyBold,
                                    marginLeft: 16,
                                }}
                            >
                                My Video
                            </div>
                            <ButtonSymphony
                                className="imageContentArtworkPreviewButton"
                                style={{ marginLeft: 'auto' }}
                                onClick={() => setShowReplaceVideoModal(true)}
                                variant="text"
                                color="purple"
                            >
                                Replace
                            </ButtonSymphony>
                        </div>
                    }
                </div>

                <WebsiteBuilderFooter
                    className="videoContentFooter"
                    contentIndex={contentIndex}
                    requiresPaidPlan={requiresPaidPlan}
                    isBackgroundColor
                    styleOptions={
                        <OptionsSymphony
                            className="videoStyleOptions"
                            style={{ flexWrap: 'wrap' }}
                            options={VIDEO_STYLE_OPTIONS}
                            value={videoContentStyle || ''}
                            handleOptionSelected={(option) => {
                                const updatedContent = [...websiteContent];
                                updatedContent[contentIndex].videoContent.style = option as VideoContentStyle;
                                setWebsiteContent(updatedContent);
                            }}
                            size="small"
                            isRadio={false}
                        />
                    }
                    titleRequired="This video style requires a paid plan to publish your website."
                />
            </CardSymphony>

            {/* MODALS */}
            <ModalSymphony
                className="replaceVideoModal"
                isOpen={showReplaceVideoModal}
                onClose={() => setShowReplaceVideoModal(false)}
                title="Choose Video"
                subtitle="Choose a video to display on your website."
                style={{ gap: 16 }}
            >
                <div
                    className="videoContentModalButtonsContainer"
                    style={sty.videoContentModalButtonsContainer}
                >
                    {renderYouTubeUrlInput()}
                    {renderVideoDivider()}
                    {renderFileUploadButton()}
                </div>
            </ModalSymphony>
        </>
    )
}

const sty: Record<string, CSSProperties> = {
    videoContentComponent: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    },
    videoContentYouTubeUrlInputContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        justifyContent: 'center',
    },
    videoContentModalButtonsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        justifyContent: 'center',
    },
    youtubeSearchContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: 8,
        width: '100%'
    },
    videoContentDividerContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        maxWidth: 360,
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    videoContentDividerLine: {
        flex: 1,
        height: 1,
        backgroundColor: Colors.grey,
    },
    videoContentDividerText: {
        ...Sty.text16greyBold,
        textAlign: 'center',
    }
}

export default VideoContent;